import _get from 'lodash/get';
import { call, put, select } from 'redux-saga/effects';

import RavenError from 'ravenjs/lib/Error';

import { MFA_METHODS } from 'constants/mfa';
import { catchError } from 'utils/errors';
import { verifyMFACodeAPI } from 'modules/apis/mfa';
import * as authActions from 'modules/auth/actions';
import { _loginWithAccessTokenSaga, _postLoginRedirectSaga } from 'modules/auth/sagas/login';

import * as mfaActions from '../actions';
import * as mfaSelectors from '../reducer';

/**
 * Verify the MFA code for the current MFA session and, on success, complete
 * the regular sign in flow with the access token returned by the API.
 *
 * NOTE: the error is stored as is, the verify identity screen maps its
 * `errorCode` to the error state it renders.
 *
 * @method verifyMFACodeSaga
 * @type   {Generator}
 * @param  {Object}          action The action holding the code to verify
 */
export function* verifyMFACodeSaga(action = {}) {
    try {
        yield put(mfaActions.verifyMFACode.request());

        const code = _get(action, 'payload.code', '');
        const mfaToken = yield select(mfaSelectors.getMFAToken);

        if (!mfaToken) {
            throw new RavenError('There is no MFA session in progress.');
        }

        const enrolledMethod = yield select(mfaSelectors.getMFAMethod);
        const method = _get(action, 'payload.method', '') || enrolledMethod || MFA_METHODS.TOTP;
        const loginContext = yield select(mfaSelectors.getMFALoginContext);

        const response = yield call(verifyMFACodeAPI, { code, method, mfaToken });
        const token = _get(response, 'data', {});
        const accessToken = _get(token, 'access_token', '');

        if (!accessToken) {
            throw new RavenError('MFA verification failed.');
        }

        // Store the token the same way the regular sign in flow does,
        // so every following request is authorized with the access token.
        yield put(authActions.fetchToken.success(token));

        const loginResult = yield call(_loginWithAccessTokenSaga, accessToken);
        catchError(loginResult);

        yield put(mfaActions.verifyMFACode.success());
        // The MFA flow is complete, drop the `mfaToken` from state.
        yield put(mfaActions.clearMFASession());
        yield call(_postLoginRedirectSaga, loginContext);
    } catch (error) {
        yield put(mfaActions.verifyMFACode.error(error));
    }
}

export default verifyMFACodeSaga;
